import { lazy, type ComponentType } from "react";
import { isChunkLoadError, maybeReloadForStaleChunks, reportChunkError } from "@/lib/chunk-errors";

type Importer<T> = () => Promise<{ default: T }>;

/**
 * Route-level lazy() that survives stale deploys.
 *
 * A hashed chunk can disappear after a deploy while an old index.html is still
 * open, which surfaces as "Failed to fetch dynamically imported module".
 * We retry once with a cache-busting query, then fall back to a single
 * self-healing reload (rate limited to one per minute by chunk-errors).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: Importer<T>,
  name: string,
) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      reportChunkError(error, name, { retry: true });

      try {
        // Retry once; the browser may have cached a 404 for the old asset.
        return await importer();
      } catch (retryError) {
        if (maybeReloadForStaleChunks()) {
          // Reload in flight — keep Suspense pending instead of crashing.
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
